import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertCircle, Mail } from 'lucide-react';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import logo from '@/assets/logo.png';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import { tracker } from '@/lib/analytics';

export default function Auth() {
  const { t, language, setLanguage } = useLanguage();

  const authSchema = z.object({
    email: z.string().email(t('auth', 'validEmail')),
    password: z.string().min(6, t('auth', 'passwordLength')),
  });

  const searchParams = new URLSearchParams(window.location.search);
  const modeParam = searchParams.get('mode');

  const [isLogin, setIsLogin] = useState(modeParam !== 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');

  const { signIn, signUp, user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Declarative redirect when user is already authenticated
  if (user) {
    return <Navigate to="/studio" replace />;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Security: Normalize auth errors to prevent email enumeration (LOGIN only)
  const getLoginErrorMessage = (err: Error): string => {
    const message = err.message.toLowerCase();

    if (
      message.includes('invalid') ||
      message.includes('credentials') ||
      message.includes('not found') ||
      message.includes('password') ||
      message.includes('user')
    ) {
      return t('auth', 'invalidCredentials');
    }

    if (message.includes('rate') || message.includes('limit')) {
      return t('auth', 'tooManyAttempts');
    }

    return t('auth', 'genericError');
  };

  // For SIGNUP we show a clearer (non-enumerating) message but include details for debugging
  const getSignupErrorMessage = (err: Error): string => {
    const message = err.message.toLowerCase();

    if (message.includes('disposable')) {
      return t('auth', 'disposableEmailError');
    }

    // Keep this generic, but show the backend message for debugging the 422.
    return `${t('auth', 'unableToCreate')} (${err.message})`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validation = authSchema.safeParse({ email, password });
    if (!validation.success) {
      setError(validation.error.errors[0].message);
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) {
          setError(getLoginErrorMessage(error));
        } else {
          tracker.track('login', { method: 'email' });
          toast({ title: t('auth', 'welcomeToast') });
          navigate('/studio');
        }
      } else {
        const { error } = await signUp(email, password);
        if (error) {
          setError(getSignupErrorMessage(error));
        } else {
          tracker.track('sign_up', { method: 'email' });
          setSignupEmail(email);
          setSignupSuccess(true);
        }
      }
    } catch {
      setError(t('auth', 'genericError'));
    } finally {
      setLoading(false);
    }
  };

  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src={logo} alt="Scalemage" className="h-8 w-8" />
            <span className="text-2xl font-bold">Scalemage</span>
          </div>
          <div className="bg-card border border-border rounded-xl p-8">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h2 className="text-xl font-semibold mb-2">{t('auth', 'emailSentTitle')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('auth', 'emailSentDescription').replace('{email}', signupEmail)}
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setSignupSuccess(false);
                setIsLogin(true);
                setPassword('');
                setError(null);
              }}
            >
              {t('auth', 'backToLogin')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src={logo} alt="Scalemage" className="h-8 w-8" />
            <span className="text-2xl font-bold">Scalemage</span>
          </div>
          <p className="text-muted-foreground">
            {isLogin ? t('auth', 'welcomeBack') : t('auth', 'createAccount')}
          </p>
          <button
            onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
            className="mt-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {language === 'en' ? 'ES' : 'EN'}
          </button>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="break-words">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth', 'email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="bg-secondary/30"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth', 'password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="bg-secondary/30"
              />
              {!isLogin && <PasswordStrengthIndicator password={password} />}
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isLogin ? (
                t('auth', 'signInButton')
              ) : (
                t('auth', 'createAccountButton')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError(null);
              }}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {isLogin ? t('auth', 'noAccount') : t('auth', 'hasAccount')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
